import React, { useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import BrandMark from "@/components/BrandMark";
import { Check, ArrowLeft, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { trackLead } from "@/lib/analytics";

const PASOS = [
  "Nos cuentas tu negocio (2 min).",
  "Montamos y configuramos tus agentes con tu info y tus canales.",
  "Lo dejamos funcionando y atendiendo — tú solo revisas resultados.",
];

const INCLUYE = [
  "Asistente que atiende a tus clientes 24/7 (web y WhatsApp)",
  "Reservas y citas automáticas, con aviso al instante",
  "Captación de clientes hecha por ti",
  "Configuración, puesta en marcha y soporte incluidos",
  "Sin que toques nada técnico ni claves de IA",
];

export default function LeadService() {
  const [form, setForm] = useState({ name: "", business: "", sector: "", contact: "", message: "" });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const F = (k) => ({
    value: form[k],
    onChange: (e) => setForm({ ...form, [k]: e.target.value }),
    className: "w-full rounded-none bg-[var(--bg)] border border-[var(--border)] px-3 h-11 text-sm text-white outline-none focus:border-[var(--accent)]",
  });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.contact.trim()) {
      toast.error("Pon al menos tu nombre y un teléfono o email");
      return;
    }
    setLoading(true);
    try {
      await api.post("/public/lead", { ...form, kind: "gestionado" });
      trackLead();
      setDone(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || "No se pudo enviar. Inténtalo de nuevo.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[var(--bg)]" data-testid="service-page">
      {/* Cabecera */}
      <header className="border-b border-[var(--border)]">
        <div className="max-w-6xl mx-auto px-6 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2.5">
            <BrandMark className="w-8 h-8" />
            <div className="leading-none">
              <div className="font-display font-black text-[15px] tracking-tight">AGENT COMPANY OS</div>
              <div className="label-mono mt-0.5">por BeautyOS</div>
            </div>
          </Link>
          <Link to="/" className="text-sm text-[var(--text-dim)] hover:text-white flex items-center gap-2">
            <ArrowLeft className="w-4 h-4" /> Volver
          </Link>
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-6 py-12 grid md:grid-cols-2 gap-10">
        {/* Izquierda: propuesta */}
        <div>
          <div className="label-mono mb-3" style={{ color: "var(--accent)" }}>HECHO POR TI · SERVICIO GESTIONADO</div>
          <h1 className="font-display font-black text-4xl md:text-5xl leading-[1.02] tracking-tight mb-5">
            Tu asistente virtual con IA,<br /><span className="text-[var(--accent)]">montado por nosotros y atendiendo 24/7.</span>
          </h1>
          <p className="text-[var(--text-dim)] leading-relaxed mb-6 max-w-md">
            Sin claves, sin ajustes, sin líos técnicos. Nosotros lo configuramos y lo dejamos
            funcionando para que atienda a tus clientes y capte negocio por ti. <strong className="text-white">Desde 300 €/mes.</strong>
          </p>
          <div className="flex flex-wrap gap-2 mb-8">
            {["Configuración en 48-72 h", "Sin permanencia", "Soporte incluido", "No tocas ninguna clave"].map((t) => (
              <span key={t} className="text-[11px] font-mono uppercase tracking-wide border border-[var(--border)] text-[var(--text-dim)] px-2.5 py-1">
                {t}
              </span>
            ))}
          </div>

          <div className="label-mono mb-3">CÓMO FUNCIONA</div>
          <ol className="space-y-3 mb-8">
            {PASOS.map((p, i) => (
              <li key={i} className="flex items-start gap-3 text-sm">
                <span className="w-6 h-6 shrink-0 flex items-center justify-center border border-[var(--accent)] text-[var(--accent)] font-mono text-xs">{i + 1}</span>
                <span className="text-[var(--text-dim)] leading-relaxed pt-0.5">{p}</span>
              </li>
            ))}
          </ol>

          <div className="label-mono mb-3">QUÉ INCLUYE</div>
          <ul className="space-y-2.5">
            {INCLUYE.map((f) => (
              <li key={f} className="flex items-start gap-2 text-sm">
                <Check className="w-4 h-4 mt-0.5 shrink-0 text-[var(--accent)]" />
                <span className="text-[var(--text-dim)]">{f}</span>
              </li>
            ))}
          </ul>
        </div>

        {/* Derecha: formulario */}
        <div>
          <div className="border border-[var(--border)] bg-[var(--surface)] p-6 md:p-8 md:sticky md:top-8">
            {done ? (
              <div className="text-center py-10" data-testid="lead-ok">
                <div className="w-14 h-14 mx-auto mb-4 flex items-center justify-center bg-[var(--accent)]/15">
                  <Check className="w-7 h-7 text-[var(--accent)]" />
                </div>
                <h2 className="font-display font-bold text-2xl mb-2">¡Recibido!</h2>
                <p className="text-sm text-[var(--text-dim)]">
                  Te contactamos muy pronto para montar tu equipo de IA. Revisa tu email o teléfono.
                </p>
                <Link to="/" className="inline-block mt-6 text-sm text-[var(--accent)] hover:underline">Volver al inicio</Link>
              </div>
            ) : (
              <form onSubmit={submit} className="space-y-4" data-testid="lead-form">
                <div>
                  <h2 className="font-display font-bold text-2xl tracking-tight mb-1">Pídelo sin compromiso</h2>
                  <p className="text-sm text-[var(--text-dim)] mb-2">Te llamamos y te lo montamos. Sin permanencia.</p>
                </div>
                <div>
                  <label className="label-mono">Tu nombre *</label>
                  <input {...F("name")} placeholder="Ej. Ana García" required data-testid="lead-name" />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="label-mono">Negocio</label>
                    <input {...F("business")} placeholder="Ej. Bar Ana" data-testid="lead-business" />
                  </div>
                  <div>
                    <label className="label-mono">Sector</label>
                    <input {...F("sector")} placeholder="Ej. restaurante" data-testid="lead-sector" />
                  </div>
                </div>
                <div>
                  <label className="label-mono">Teléfono o email *</label>
                  <input {...F("contact")} placeholder="Para contactarte" required data-testid="lead-contact" />
                </div>
                <div>
                  <label className="label-mono">¿Qué necesitas? (opcional)</label>
                  <textarea
                    {...F("message")}
                    rows={3}
                    className="w-full rounded-none bg-[var(--bg)] border border-[var(--border)] px-3 py-2 text-sm text-white outline-none focus:border-[var(--accent)]"
                    placeholder="Cuéntanos brevemente"
                    data-testid="lead-message"
                  />
                </div>
                <button
                  type="submit"
                  disabled={loading}
                  className="w-full py-3 text-sm font-semibold disabled:opacity-60"
                  style={{ background: "var(--accent)", color: "var(--bg)" }}
                  data-testid="lead-submit"
                >
                  {loading ? "Enviando…" : "Quiero que me lo montéis"}
                </button>
                <p className="text-center text-[12px] text-white/90 font-medium">
                  Te llamamos en menos de 24 h · Sin compromiso y sin permanencia
                </p>
                <p className="flex items-center gap-1.5 text-[11px] text-[var(--text-dim)]">
                  <ShieldCheck className="w-3.5 h-3.5 shrink-0" />
                  Al enviar aceptas nuestra{" "}
                  <Link to="/privacidad" className="text-[var(--accent)] hover:underline">Política de Privacidad</Link>.
                </p>
              </form>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
